/**
 * One playing card, wherever a card is shown.
 *
 * ## Why this is its own file
 *
 * It was defined inside `GameTable.tsx`, so the blackjack room could not reach it and
 * grew a second card of its own: a monospace pill on a slightly different cream, with
 * a red that was not `--oxblood`, and a face-down card that was a flat maroon
 * rectangle rather than the Frontier Book back. Side by side the two tables were
 * dealing from visibly different decks — and because the room could not render a card
 * back, the one thing on this site anybody can buy was invisible at an entire table.
 *
 * A card is the most-looked-at object in either game. There is one of them, and adding
 * the real art did not change that: the two faces below are two outputs of one
 * component, not two components.
 *
 * ## The two faces
 *
 * Until now every card was a rank and a suit character in a cream box — legible, and
 * nothing like a deck. `art/cards.ts` now composes the real face: the aged stock, the
 * engraved pips, the courts, the enlarged corner indices.
 *
 * Which face depends on how big the card is drawn, and the rule is measured rather
 * than chosen. Below about 90px a traditional face is a cream rectangle with a smudge
 * in the corner, so everything under that size — the board, the opponents' cards, the
 * dealer's up-card, the trainer's spot — gets the COMPACT face: the rank set large
 * across the top half with one big pip beneath it, which reads at 30px. The hero's own
 * cards and the blackjack hero's hand, which are never smaller than about 62px, get
 * the FULL face with courts.
 *
 * `faceForKind` in `art/cards.ts` is where that line is drawn, once.
 *
 * ## Sizes
 *
 * `kind` picks a measured size rather than taking a number, because the poker table's
 * three sizes encode a hierarchy that was measured rather than invented — the board's
 * cards are the largest, larger than your own hole cards, which are larger than an
 * opponent's, because shared information re-read on every street earns the most size.
 * On a table all three are sized by the art kit (`table/topdown.css`, `--card` and
 * `--hero`, off the table's own container), so nothing drifts; the classic felt's `--tw`
 * they used to hang off was retired in Phase 9.
 *
 * `bj` and `bj-hero` are the blackjack drills' two sizes (`--bj-card-w`, in
 * `rooms/bj-drills.css`; at the table the kit sizes them too), and `drill` is the
 * trainer's (`--pt-card-w`, `table/spot.css`). Every one shares the aspect ratio, the
 * radius formula and every colour with the rest.
 *
 * Three surfaces, one card. The trainer had its own until this was written: a gradient
 * ground with a serif rank and a red that was not `--oxblood`, so a player drilling a
 * spot and a player at the table were looking at two different decks.
 */
import type { CosmeticRef } from "@engine/table-state";
import { CardBack } from "../lib/mark";
import { cardName, suitIsRed, cardLabel } from "../lib/table-game";
import { cardFace, faceForKind } from "../art/cards";
import { deckBackUrl } from "../settings/prefs";

export type CardKind = "board" | "hero" | "seat" | "bj" | "bj-hero" | "drill";

/**
 * The printed back, at the half-scale tier — see the note in `art/cards.ts` about `@1x`.
 *
 * PHASE 2B: which one is the player's, from the Settings sheet. Two backs are shipped
 * (red and plum) and `deckBackUrl()` names the file; there is still exactly one card
 * component and one deck on the felt, which is what `one-deck-one-felt` is about.
 * Read at render, so changing it in the sheet reaches the felt without a reload.
 */


export function PlayingCard({ card, kind, hidden = false, back = null }: {
  card?: number;
  kind: CardKind;
  hidden?: boolean;
  /** The seat's equipped card back, if cosmetics are on. Purely visual. */
  back?: CosmeticRef | null;
}) {
  if (hidden || card === undefined) {
    /*
     * The house back is the PRINTED one; the cosmetics are the drawn one.
     *
     * `CardBack` in `lib/mark.tsx` was written before the art existed: a framed ground
     * with the mark centred, in whatever colour the equipped item asks for. The Frontier
     * Book's actual back — red scrollwork, the cartouche — has been in `public/art/`
     * since the art kit landed and nothing was using it, so every face-down card on the
     * felt was a cream rectangle with a maroon B on it. Against the mock that is the
     * loudest thing on the table: two of them behind every head, brighter than the
     * portraits.
     *
     * So: no cosmetic equipped means the house back, which is the render. An equipped
     * back is a recolour of the vector, which is what makes a purchasable back look
     * different from the house one — the whole point of the item.
     */
    return (
      <span
        className={`gc gc-${kind} gc-back${back ? ` gc-back-${back.assetKey.split("/")[1]}` : " gc-back-house"}`}
        aria-label="face-down card"
      >
        {back ? <CardBack /> : <img className="gcb-art" src={deckBackUrl()} alt="" aria-hidden="true" />}
      </span>
    );
  }
  const { suit } = cardName(card);
  /*
   * `dangerouslySetInnerHTML` with a string this module built itself, from a card
   * number and two lookup tables — there is no path from anything a player or a server
   * can type into this markup. The alternative, a data: URI in an <img>, would bake the
   * colours in and take the card off the palette, which is the thing this component
   * exists to prevent.
   */
  return (
    <span
      className={`gc gc-${kind} gc-art${suitIsRed(suit) ? " gc-red" : ""}`}
      aria-label={cardLabel(card)}
      dangerouslySetInnerHTML={{ __html: cardFace(card, faceForKind(kind)) }}
    />
  );
}
